import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";
import {
  FilterOptions,
  GapByCountyStatusResult,
  GapCountResult,
  GroupByOption,
  GroupedProjectResourcesRequest,
  NetworkPortfolioManagementProject,
  NetworkPortfolioManagementResourceView,
  NpmFilteredViewResponse,
  ProjectGroup,
  ProjectResource,
  ProjectResourcesRequest,
  ProjectsDataRequest,
  SavedViewRequest,
  SortByOption,
  SortedProjectResourcesRequest,
} from "@app/network-portfolio-management/interfaces";

export default class NetworkPortfolioManagementService extends ServiceBase {
  // The view scope is not a filter — it travels as the `view` query parameter (bound to the server's
  // NetworkPortfolioManagementResourceViewEnum) so the request body stays purely filter criteria. Passing the
  // enum (rather than a boolean) keeps the lift to any additional views straightforward.
  getProjectResources(
    request: ProjectResourcesRequest,
    view = NetworkPortfolioManagementResourceView.Active,
    signal?: AbortSignal,
  ): Promise<ProjectResource[]> {
    return this.makePost<ProjectResource[]>(
      `networkportfoliomanagement/project-resources?${querystringify({ view })}`,
      request,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal,
    );
  }

  /**
   * Returns the supplied project IDs globally sorted by sortBy, without pagination. The caller
   * caches this per ID-list+sort combination and slices client-side; each page slice (25 IDs) is
   * passed to getProjectsData, eliminating the per-page full-set reload on the backend.
   * Only call for data-driven sorts (LastUpdated, AdequacyScore, GapCount); alphabetical sorts
   * use the existing SQL ORDER BY path in getProjectsData.
   */
  getSortedProjectIds(
    projectIds: number[],
    sortBy: SortByOption,
    signal?: AbortSignal,
  ): Promise<number[]> {
    const request: SortedProjectResourcesRequest = { projectIds, sortBy };
    return this.makePost<number[]>(
      "networkportfoliomanagement/project-resources/sorted",
      request,
      undefined, false, "application/json", true, undefined, signal,
    );
  }

  /**
   * Returns the supplied project IDs partitioned by the given Group By dimension, with each
   * group's IDs pre-sorted by sortBy. The backend sorts globally before partitioning, so
   * within-group order is consistent with the global sort. The caller slices each group's ID
   * list client-side for per-group pagination.
   */
  getGroupedProjectResources(
    projectIds: number[],
    groupBy: GroupByOption,
    sortBy: SortByOption,
    signal?: AbortSignal,
  ): Promise<ProjectGroup[]> {
    const request: GroupedProjectResourcesRequest = { groupBy, projectIds, sortBy };
    return this.makePost<ProjectGroup[]>(
      "networkportfoliomanagement/project-resources/grouped",
      request,
      undefined, false, "application/json", true, undefined, signal,
    );
  }


  getProjectsData(request: ProjectsDataRequest, signal?: AbortSignal): Promise<NetworkPortfolioManagementProject[]> {
    return this.makePost<NetworkPortfolioManagementProject[]>(
      "networkportfoliomanagement/projects-data",
      request,
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal,
    );
  }

  getGapsCount(projectIds: number[], signal?: AbortSignal): Promise<GapCountResult> {
    return this.makePost<GapCountResult>(
      "networkportfoliomanagement/total-gap-count",
      { projectIds },
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal,
    );
  }

  getGapByCountyStatus(projectIds: number[], signal?: AbortSignal): Promise<GapByCountyStatusResult[]> {
    return this.makePost<GapByCountyStatusResult[]>(
      "networkportfoliomanagement/gap-by-county-status",
      { projectIds },
      undefined,
      false,
      "application/json",
      true,
      undefined,
      signal,
    );
  }

  getFilterOptions(
    view = NetworkPortfolioManagementResourceView.Active,
    signal?: AbortSignal,
  ): Promise<FilterOptions> {
    return this.makeGet<FilterOptions>(
      `networkportfoliomanagement/filter-options?${querystringify({ view })}`,
      undefined,
      undefined,
      false,
      undefined,
      signal,
    );
  }

  getSavedViews(signal?: AbortSignal): Promise<NpmFilteredViewResponse[]> {
    return this.makeGet<NpmFilteredViewResponse[]>(
      "networkportfoliomanagement/filtered-views",
      undefined,
      undefined,
      false,
      undefined,
      signal,
    );
  }

  createSavedView(request: SavedViewRequest): Promise<NpmFilteredViewResponse> {
    return this.makePost<NpmFilteredViewResponse>(
      "networkportfoliomanagement/filtered-views",
      request,
      undefined,
      false,
      "application/json",
      true,
    );
  }

  updateSavedView(id: number, request: SavedViewRequest): Promise<NpmFilteredViewResponse> {
    return this.makePut<NpmFilteredViewResponse>(
      `networkportfoliomanagement/filtered-views/${id}`,
      request,
      undefined,
      false,
      "application/json",
    );
  }

  deleteSavedView(id: number): Promise<void> {
    return this.makeDelete<void>(`networkportfoliomanagement/filtered-views/${id}`);
  }
}
